import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
  icon?: LucideIcon;
}

/**
 * Segmented control — switch between mutually-exclusive VIEWS of the same data
 * (Schedule views, Production sub-views, review tabs). 2–4 items.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div
      className={cn("segs workspace-control-group", className)}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={cn("seg workspace-control", o.value === value && "seg--on")}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <Icon icon={o.icon} size={14} />}
          {o.label}
          {o.count != null && <span className="seg__n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Primary tabs — top-level DESTINATIONS within a detail page (underline style),
 * distinct from the pill segmented control used for views inside a destination.
 * 3–5 tabs.
 */
export function PrimaryTabs<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <nav className={cn("ptabs", className)} role="tablist" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={cn("ptab", o.value === value && "ptab--on")}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <Icon icon={o.icon} size={16} />}
          {o.label}
          {o.count != null && <span className="ptab__n">{o.count}</span>}
        </button>
      ))}
    </nav>
  );
}

/**
 * Section header — labels a content group with an icon + a trailing rule that
 * fills the remaining width. Optional mono count after the title.
 */
export function SectionHeader({
  icon,
  title,
  count,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  count?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("sec-head", className)}>
      {icon && (
        <span className="sec-head__icon">
          <Icon icon={icon} size={14} />
        </span>
      )}
      <h3 className="sec-head__title">{title}</h3>
      {count != null && (
        <span className="font-mono text-[10.5px] text-ink-4">{count}</span>
      )}
    </div>
  );
}
